import { useEffect, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';

import { trackEvent } from '../utils/analytics';
import type { StepManagerContextType } from '../steps/types/step_manager';
import { UPDATE_USER_MUTATION } from '../apollo/graphql/update_user.mutation';
import type { UpdateUserData, UpdateUserInput } from '../apollo/graphql/update_user.mutation';
import { getClient } from '../apollo';

/**
 * Hook to detect machine translation (Google Translate, etc.) based on DOM changes
 * Uses the method described in: https://martijnhols.nl/blog/how-to-detect-google-translate-and-other-machine-translation
 */
export const useTrackTranslation = (context: StepManagerContextType, route: string) => {
  const { data } = context;
  const originalLanguageRef = useRef<string>('');
  const observersRef = useRef<MutationObserver[]>([]);
  const client = getClient();

  const [updateUser] = useMutation<UpdateUserData, UpdateUserInput>(UPDATE_USER_MUTATION, { client });

  const handleTranslationDetected = useCallback(
    (originalLang: string, newLang: string, method: 'generic' | 'edge') => {
      console.info(
        `Page translation detected (${method === 'generic' ? 'Generic' : 'Microsoft Edge'})`,
        `lang="${newLang}" (original="${originalLang}")`
      );

      // Track analytics event
      void trackEvent('TranslationDetected', {
        eventMetadata: {
          originalLanguage: originalLang,
          newLanguage: newLang,
          detectionMethod: method,
          step: route,
        },
      });

      // If newLang is not the same as last translated language, update the appData.
      // Only do this when user is onboarding. When stepSection changes, browser won't translate automatically, users need to turn on translation manually.
      // If users gave up before onboarding, users will not reach finish step, so it's meaningless for these users to enter ABTest
      if (newLang !== data.appData?.signupParams?.translatedTo && data?.appData?.isSigned) {
        // Save to backend
        void updateUser({
          variables: {
            input: {
              translatedTo: newLang,
            },
          },
        });
      }
    },
    [data, route, updateUser]
  );

  useEffect(() => {
    // Store the original language
    originalLanguageRef.current = document.documentElement.lang || 'en';

    // Generic detector for Chrome, Firefox, Safari and most browsers
    const langObserver = new MutationObserver(() => {
      const currentLang = document.documentElement.lang;
      const originalLang = originalLanguageRef.current;

      if (currentLang !== originalLang) {
        // Translation was applied
        handleTranslationDetected(originalLang, currentLang, 'generic');
      }
    });

    langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
      childList: false,
      characterData: false,
    });

    observersRef.current.push(langObserver);

    // Microsoft Edge specific detector
    const titleElement = document.getElementsByTagName('title')[0];

    if (titleElement) {
      const edgeObserver = new MutationObserver(() => {
        if (titleElement.hasAttribute('_msttexthash')) {
          handleTranslationDetected(originalLanguageRef.current, 'unknown', 'edge');
        }
      });

      edgeObserver.observe(titleElement, {
        attributes: true,
        attributeFilter: ['_msttexthash'],
        childList: false,
        characterData: false,
      });

      observersRef.current.push(edgeObserver);
    }

    // Cleanup function
    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
      observersRef.current = [];
    };
  }, [handleTranslationDetected]);
};
